import {
  TICKET_STATE_LABELS,
  canCloseOrReopen,
  canDiscard,
  manualStateTargets,
  type DiscardTicketInput,
  type TicketDetail,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useSession } from '../../api/auth';
import { useQueueSettings } from '../../api/queue-settings';
import { useChangeTicketState, useCloseOrReopenTicket, useDiscardTicket } from '../../api/ticket-closure';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { personName } from '../../components/ticket';
import { DetailSection } from './DetailSection';
import { DiscardTicketDialog } from './DiscardTicketDialog';

type Dialog = 'scarta' | 'close' | 'reopen' | null;

/** Che cosa vuol dire lo stato per chi lavora il ticket, e che cosa si può fare. */
function stateExplanation(state: TicketDetail['state'], days: number | undefined): string {
  switch (state) {
    case 'risolto':
      return days === undefined
        ? 'Risolto: la correzione è stata comunicata a chi ha segnalato. Se risponde, il ticket si riapre.'
        : `Risolto: la correzione è stata comunicata a chi ha segnalato. Se risponde, il ticket si riapre; senza replica passa a «Chiuso» dopo ${days} giorni.`;
    case 'chiuso':
      return 'Chiuso: definitivo, non si riapre. Una nuova segnalazione sullo stesso tema è un nuovo ticket.';
    case 'scartato':
      return 'Scartato: definitivo, nessuna email a chi ha segnalato.';
    case 'nuovo':
      return 'Nuovo: si prende in carico con l’assegnazione o con la prima risposta. «Risolto» si raggiunge solo con «Invia e chiudi» dal composer qui sotto.';
    default:
      return '«Risolto» si raggiunge solo con «Invia e chiudi» dal composer qui sotto: parte sempre l’email di chiusura a chi ha segnalato.';
  }
}

/**
 * Chi può chiudere o riaprire un ticket risolto. Senza un responsabile nel team (mai assegnato o
 * uscito dal team) lo si assegna dal blocco «In carico a», che resta modificabile su un Risolto.
 */
function responsibleNotice(assignee: TicketDetail['assignee']): string {
  if (assignee === null) {
    return 'Riaprire o chiudere un ticket risolto spetta al responsabile: il ticket non ne ha uno, assegnalo dal blocco «In carico a».';
  }
  if (assignee.removed) {
    return `Riaprire o chiudere un ticket risolto spetta al responsabile: ${personName(assignee)} non è più nel team, assegna un nuovo responsabile dal blocco «In carico a».`;
  }
  return `Riaprire o chiudere un ticket risolto spetta al responsabile, ${personName(assignee)}.`;
}

/**
 * Stato e chiusura del ticket (DEC-002, story #6): cambio di stato manuale fra «In lavorazione» e
 * «In attesa dell'utente» (mai verso «Risolto»), «Scarta» con motivo da ogni stato non terminale,
 * e su un ticket «Risolto» «Chiudi» e «Riapri», abilitati **solo per il responsabile** (l'API
 * risponde 403 a chiunque altro). Ogni azione è un evento nel thread.
 */
export function TicketStateSection({ ticket }: { ticket: Pick<TicketDetail, 'code' | 'state' | 'assignee'> }) {
  const [dialog, setDialog] = useState<Dialog>(null);
  const [done, setDone] = useState<{ code: string; text: string } | null>(null);
  const session = useSession();
  const settings = useQueueSettings();
  const changeState = useChangeTicketState(ticket.code);
  const discard = useDiscardTicket(ticket.code);
  const closeOrReopen = useCloseOrReopenTicket(ticket.code);

  const targets = manualStateTargets(ticket.state);
  const resolved = canCloseOrReopen(ticket.state);
  const isResponsible = ticket.assignee !== null && ticket.assignee.id === session.data?.member.id;
  const busy = changeState.isPending || discard.isPending || closeOrReopen.isPending;
  const feedback = done?.code === ticket.code ? done.text : null;

  function confirmDiscard(input: DiscardTicketInput) {
    discard.mutate(input, {
      onSuccess: () => {
        setDialog(null);
        setDone({ code: ticket.code, text: 'Ticket scartato. Nessuna email a chi ha segnalato.' });
      },
    });
  }

  function confirmCloseOrReopen(action: 'close' | 'reopen') {
    closeOrReopen.mutate(action, {
      onSuccess: () => {
        setDialog(null);
        setDone({
          code: ticket.code,
          text: action === 'close' ? 'Ticket chiuso: è definitivo.' : 'Ticket riaperto: è di nuovo in lavorazione.',
        });
      },
    });
  }

  function openDialog(next: Dialog) {
    discard.reset();
    closeOrReopen.reset();
    setDialog(next);
  }

  return (
    <DetailSection title="Stato e chiusura">
      <Typography variant="body2" color="text.secondary">
        {stateExplanation(ticket.state, settings.data?.daysResolvedToClosed)}
      </Typography>

      {(targets.length > 0 || canDiscard(ticket.state)) && (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {targets.map((target) => (
            <Button
              key={target}
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={() =>
                changeState.mutate(target, {
                  onSuccess: () =>
                    setDone({ code: ticket.code, text: `Il ticket è «${TICKET_STATE_LABELS[target]}».` }),
                })
              }
            >
              Passa a «{TICKET_STATE_LABELS[target]}»
            </Button>
          ))}
          {resolved && (
            <>
              <Button
                variant="outlined"
                size="small"
                disabled={busy || !isResponsible}
                onClick={() => openDialog('reopen')}
              >
                Riapri
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={busy || !isResponsible}
                onClick={() => openDialog('close')}
              >
                Chiudi
              </Button>
            </>
          )}
          {canDiscard(ticket.state) && (
            <Button variant="outlined" color="error" size="small" disabled={busy} onClick={() => openDialog('scarta')}>
              Scarta
            </Button>
          )}
        </Stack>
      )}

      {resolved && !isResponsible && (
        <Alert severity="info" role="note">
          {responsibleNotice(ticket.assignee)}
        </Alert>
      )}
      {changeState.isError && <Alert severity="error">Stato non cambiato: {changeState.error.message}</Alert>}
      {feedback && (
        <Alert severity="success" role="status">
          {feedback}
        </Alert>
      )}

      {dialog === 'scarta' && (
        <DiscardTicketDialog
          open
          code={ticket.code}
          busy={discard.isPending}
          error={discard.isError ? discard.error.message : null}
          onConfirm={confirmDiscard}
          onClose={() => setDialog(null)}
        />
      )}
      {(dialog === 'close' || dialog === 'reopen') && (
        <ConfirmDialog
          open
          title={dialog === 'close' ? `Chiudi il ticket ${ticket.code}` : `Riapri il ticket ${ticket.code}`}
          confirmLabel={dialog === 'close' ? 'Chiudi' : 'Riapri'}
          busy={closeOrReopen.isPending}
          error={closeOrReopen.isError ? closeOrReopen.error.message : null}
          onConfirm={() => confirmCloseOrReopen(dialog)}
          onClose={() => setDialog(null)}
        >
          {dialog === 'close'
            ? '«Chiuso» è definitivo: il ticket non si riapre e una nuova segnalazione sullo stesso tema sarà un nuovo ticket. Nessuna email a chi ha segnalato.'
            : 'Il ticket torna «In lavorazione» e la riapertura conta nelle statistiche. Nessuna email a chi ha segnalato.'}
        </ConfirmDialog>
      )}
    </DetailSection>
  );
}
